// @ts-nocheck
/**
 * OrderByStoreContent — Custom grid, Stores cycled in right panel, SKUs shown in grid
 *
 * Mirror of OrderBySkuContent but pivoted: cycle through stores (instead of SKUs),
 * grid shows SKUs for the selected store, distribute by SKU attributes (Brand, Size, etc.).
 * No availability cards — availability info is shown in the grid columns.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { REQUESTS } from 'utils/services/thetaretail/requests';
import { putAPIDataV3 } from 'utils/api';
import { removeItemFromLocalStorage } from 'utils/storage';
import { updateSelectedRows } from 'models/dynamicGrid/reducer';
import StoreSelectionBox from './components/storeSelectionBox';
import DynamicFilterBox from 'pages/orderBySku/components/DynamicFilterBox';
import AgStoreOrderGrid from './components/AgStoreOrderGrid';
import DistributeModal from 'pages/orderBySku/components/distributeModal';
import { Dot9loader } from 'molecules/dot9loader';
import * as obsSagaActions from './sagaActions';
import { fetchBookmarksList } from 'models/bookmarks/sagaActions';
import {
	SELECT_ROW_KEY,
	BULK_ITEMS_PER_PAGE,
	VALIDATION_ERROR_DISPLAY_MS,
	DEBOUNCE_SAVE_MS,
	DEFAULT_EXPORT_FILENAME,
} from './constants';
import { getObsStoreColumns, getObsStoreOrderData, getObsStoreLoading } from './selector';
import './orderByStore.scss';

interface OrderByStoreContentProps {
	tableId: number;
	orderStatusValue?: string;
}

const OrderByStoreContent = forwardRef<any, OrderByStoreContentProps>(({ tableId, orderStatusValue = '3' }, ref) => {
	const dispatch = useDispatch();
	const gridApiRef = useRef<any>(null);
	useMemo(() => {
		try {
			removeItemFromLocalStorage(tableId);
		} catch (e) {}
	}, [tableId]);

	/* ─── Direct API save buffer (bypasses saga — reliable even without Redux grid metadata) ─── */
	const bulkBuffer = useRef<Record<string, any>>({});
	const lastEditRow = useRef<any>(null);

	/** Send buffered edits directly to the UPDATE_ROW_DATA API */
	const flushSaveRef = useRef<(edits: Record<string, any>, refRow: any, orderKey: string) => Promise<void>>();
	flushSaveRef.current = async (edits: Record<string, any>, refRow: any, orderKey: string) => {
		const editValues = Object.values(edits);
		if (!refRow || !editValues.length) {
			console.warn('[OBS-Store] flushSave skipped — no refRow or empty edits');
			return;
		}
		const keyValue = refRow[SELECT_ROW_KEY];
		if (keyValue === undefined || keyValue === null) {
			console.error(`[OBS-Store] CRITICAL: ${SELECT_ROW_KEY} not found in row!`);
			return;
		}
		try {
			const orderId = refRow['OrderID'];
			const groupSearchParams = orderId != null ? [{ column: 'OrderID', value: orderId, type: 'number' }] : [];

			const body = {
				tableId,
				updatedValue: refRow[orderKey],
				rowIdentifier: orderKey,
				selectedRowKeyName: SELECT_ROW_KEY,
				selectedRowKeyValue: keyValue,
				BulkUpdateJSON: editValues,
				isMultiRowUpdate: true,
				pageNumber: 1,
				itemsPerPage: BULK_ITEMS_PER_PAGE,
				groupSearchParams,
				search: JSON.stringify({ filter: [] }),
				isAdvancedSearch: 1,
			};
			const response = await putAPIDataV3({
				service: REQUESTS.PUT.UPDATE_ROW_DATA.service,
				url: REQUESTS.PUT.UPDATE_ROW_DATA.url,
				body,
			});
			const respData = response?.data || response;

			/* Handle success: apply returned row data to allOrderDataRef */
			const updatedRows = respData?.updatedRows;
			const updateAvlQty = respData?.updateAvlQty;
			if (Array.isArray(updatedRows) && updatedRows.length > 0) {
				const returnedMap = new Map<string, any>();
				updatedRows.forEach((r: any) => {
					const id = r[SELECT_ROW_KEY] ?? r['OrderDetailID'];
					if (id != null) returnedMap.set(String(id), r);
				});

				if (Array.isArray(updateAvlQty)) {
					updateAvlQty.forEach((avl: any) => {
						const id = String(avl.RowIdentifier ?? avl[SELECT_ROW_KEY] ?? '');
						if (id && !returnedMap.has(id)) returnedMap.set(id, avl);
					});
				}

				const data = allOrderDataRef.current;
				const refreshNodes: any[] = [];
				const gridApi = gridApiRef.current;
				for (let i = 0; i < data.length; i++) {
					const rowId = String(data[i][SELECT_ROW_KEY] ?? data[i]['OrderDetailID'] ?? '');
					const returned = returnedMap.get(rowId);
					if (!returned) continue;
					Object.keys(returned).forEach((key) => {
						if (key === 'RowIdentifier') return;
						const val = returned[key];
						data[i][key] = val && typeof val === 'object' && val.value !== undefined ? val.value : val;
					});
					if (gridApi) {
						const node = gridApi.getRowNode(rowId);
						if (node) refreshNodes.push(node);
					}
				}

				if (gridApi && refreshNodes.length > 0) {
					gridApi.refreshCells({ rowNodes: refreshNodes, force: true });
				}
				bumpMetrics();
			}

			// Handle backend validation errors
			if (respData?.validationErrors?.length > 0) {
				const gridApi = gridApiRef.current;
				if (gridApi) {
					const errorMap = new Map();
					respData.validationErrors.forEach((ve: any) => {
						errorMap.set(String(ve.RowIdentifier), ve.ErrorMessage || 'Server validation failed');
					});
					const affectedNodes: any[] = [];
					gridApi.forEachNode((node: any) => {
						if (node.data && errorMap.has(String(node.data.OrderDetailID))) {
							const errorMsg = errorMap.get(String(node.data.OrderDetailID));
							node.data.OrderQty = 0;
							node.data._availError = errorMsg;
							affectedNodes.push(node);
							const masterRow = allOrderDataRef.current.find(
								(r) => String(r.OrderDetailID) === String(node.data.OrderDetailID)
							);
							if (masterRow) masterRow.OrderQty = 0;
						}
					});
					if (affectedNodes.length > 0) {
						gridApi.refreshCells({ rowNodes: affectedNodes, columns: ['OrderQty'], force: true });
						setTimeout(() => {
							const stillValid: any[] = [];
							affectedNodes.forEach((node) => {
								if (node.data?._availError) {
									delete node.data._availError;
									stillValid.push(node);
								}
							});
							if (stillValid.length > 0) {
								try {
									gridApi.refreshCells({ rowNodes: stillValid, columns: ['OrderQty'], force: true });
								} catch (e) {}
							}
						}, VALIDATION_ERROR_DISPLAY_MS);
					}
					bumpMetrics();
				}
			}
		} catch (e: any) {
			console.error('[OBS-Store] Save FAILED:', e?.message || e, e?.response?.data || '');
		}
	};

	const debouncedSendBulk = useRef(
		_.debounce(() => {
			const row = lastEditRow.current;
			if (!row || !Object.keys(bulkBuffer.current).length) return;
			const orderKey = Object.keys(row).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			flushSaveRef.current?.({ ...bulkBuffer.current }, row, orderKey);
			bulkBuffer.current = {};
		}, DEBOUNCE_SAVE_MS)
	).current;

	useEffect(() => {
		return () => {
			if (debouncedSendBulk?.cancel) debouncedSendBulk.cancel();
		};
	}, [debouncedSendBulk]);

	/* 1) Columns (via saga) */
	useEffect(() => {
		dispatch(obsSagaActions.fetchStoreColumns({ tableId }));
	}, [tableId]);
	useEffect(() => {
		dispatch(fetchBookmarksList({ tableId: String(tableId) }));
	}, [tableId]);
	const { skuColumns, storeColumns, warehouseColumns, orderColumns } = useSelector(getObsStoreColumns);

	const [storeIndex, setStoreIndex] = useState(0);

	const [skuActiveKeys, setSkuActiveKeys] = useState<string[]>([]);
	const [skuFilterValues, setSkuFilterValues] = useState<Record<string, string>>({});
	const [storeActiveKeys, setStoreActiveKeys] = useState<string[]>([]);
	const [storeFilterValues, setStoreFilterValues] = useState<Record<string, string>>({});
	const [whActiveKeys, setWhActiveKeys] = useState<string[]>([]);
	const [whFilterValues, setWhFilterValues] = useState<Record<string, string>>({});
	const [orderActiveKeys, setOrderActiveKeys] = useState<string[]>([]);
	const [orderFilterValues, setOrderFilterValues] = useState<Record<string, string>>({});

	const [massMetric, setMassMetric] = useState('flat');
	const [massBaseQty, setMassBaseQty] = useState(0);
	const [distributeOpen, setDistributeOpen] = useState(false);
	const [overflowOpen, setOverflowOpen] = useState(false);
	const [overflowInfo, setOverflowInfo] = useState<any>(null);

	/* 2) Fetch order data */
	const [refreshKey, setRefreshKey] = useState(0);
	useEffect(() => {
		dispatch(obsSagaActions.fetchStoreOrderData({ tableId, orderStatusValue, refreshKey }));
	}, [tableId, orderStatusValue, refreshKey]);
	const obsOrderData = useSelector(getObsStoreOrderData);
	const obsLoading = useSelector(getObsStoreLoading);
	const dataLoading = obsLoading.data;
	const backgroundLoading = obsLoading.backgroundData;

	/* Mutable ref for in-place cell edits + version counters */
	const allOrderDataRef = useRef<any[]>([]);
	const [dataVersion, setDataVersion] = useState(0);
	const [metricsVersion, setMetricsVersion] = useState(0);
	const bumpData = useCallback(() => {
		setDataVersion((v) => v + 1);
		setMetricsVersion((v) => v + 1);
	}, []);
	const bumpMetrics = useCallback(() => setMetricsVersion((v) => v + 1), []);

	useEffect(() => {
		if (obsOrderData.rows.length > 0) {
			allOrderDataRef.current = obsOrderData.rows.map((r) => ({ ...r }));
			bumpData();
		}
	}, [obsOrderData.rows]);
	const allOrderData = allOrderDataRef.current;

	/* Expose refresh / resetView to parent */
	useImperativeHandle(
		ref,
		() => ({
			refresh: () => setRefreshKey((k) => k + 1),
			resetView: () => {
				if (gridApiRef.current) {
					gridApiRef.current.resetColumnState();
					gridApiRef.current.setFilterModel(null);
					gridApiRef.current.applyColumnState({ defaultState: { sort: null } });
				}
			},
			exportData: (fileName?: string) => {
				if (gridApiRef.current) {
					gridApiRef.current.exportDataAsCsv({
						fileName: fileName || DEFAULT_EXPORT_FILENAME,
						columnKeys: gridApiRef.current
							.getAllDisplayedColumns()
							?.filter((c) => c.getColId() !== 'ag-Grid-SelectionColumn')
							.map((c) => c.getColId()),
					});
				}
			},
		}),
		[]
	);

	/* Sync row selection to Redux */
	const handleSelectionChanged = useCallback(
		(selectedRows: any[]) => {
			const ids = selectedRows.map((r) => r.OrderDetailID).filter(Boolean);
			dispatch(
				updateSelectedRows({
					tableId,
					data: { selectedRowKeys: ids, isSelectAll: false, rowGroup: null },
				})
			);
		},
		[tableId, dispatch]
	);

	/* 3) Store list derived from order data */
	const storeFieldName = useMemo(() => {
		if (!allOrderData.length) return 'Store';
		return Object.keys(allOrderData[0] || {}).find((k) => /^store$/i.test(k)) || 'Store';
	}, [dataVersion]);

	const storeDescFieldName = useMemo(() => {
		if (!allOrderData.length) return '';
		const keys = Object.keys(allOrderData[0] || {});
		return keys.find((k) => /^storedesc/i.test(k)) || keys.find((k) => /store.*desc/i.test(k)) || '';
	}, [dataVersion]);

	/* SKU field name (for grid entity) */
	const skuFieldName = useMemo(() => {
		if (!allOrderData.length) return 'SKU';
		return Object.keys(allOrderData[0] || {}).find((k) => /^sku$/i.test(k)) || 'SKU';
	}, [dataVersion]);

	/* 3a) Cross-filtering */
	const applyFilters = useCallback((data, filterVals) => {
		const active = Object.entries(filterVals).filter(([, v]) => v && v !== 'all');
		if (!active.length) return data;
		return data.filter((row) => active.every(([key, val]) => String(row[key] || '') === val));
	}, []);

	const allFilters = useMemo(
		() => ({ ...skuFilterValues, ...storeFilterValues, ...whFilterValues, ...orderFilterValues }),
		[skuFilterValues, storeFilterValues, whFilterValues, orderFilterValues]
	);
	const fullyFilteredData = useMemo(() => applyFilters(allOrderData, allFilters), [dataVersion, allFilters]);
	const dataForStoreFilter = useMemo(
		() => applyFilters(allOrderData, { ...skuFilterValues, ...whFilterValues, ...orderFilterValues }),
		[dataVersion, skuFilterValues, whFilterValues, orderFilterValues]
	);
	const dataForSkuFilter = useMemo(
		() => applyFilters(allOrderData, { ...storeFilterValues, ...whFilterValues, ...orderFilterValues }),
		[dataVersion, storeFilterValues, whFilterValues, orderFilterValues]
	);
	const dataForWhFilter = useMemo(
		() => applyFilters(allOrderData, { ...skuFilterValues, ...storeFilterValues, ...orderFilterValues }),
		[dataVersion, skuFilterValues, storeFilterValues, orderFilterValues]
	);
	const dataForOrderFilter = useMemo(
		() => applyFilters(allOrderData, { ...skuFilterValues, ...storeFilterValues, ...whFilterValues }),
		[dataVersion, skuFilterValues, storeFilterValues, whFilterValues]
	);

	const mkToggle = (setter, valSetter) => (key) => {
		setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
		valSetter((prev) => {
			const n = { ...prev };
			delete n[key];
			return n;
		});
	};
	const handleSkuToggle = useCallback(mkToggle(setSkuActiveKeys, setSkuFilterValues), []);
	const handleStoreToggle = useCallback(mkToggle(setStoreActiveKeys, setStoreFilterValues), []);
	const handleWhToggle = useCallback(mkToggle(setWhActiveKeys, setWhFilterValues), []);
	const handleOrderToggle = useCallback(mkToggle(setOrderActiveKeys, setOrderFilterValues), []);

	/* Store sort state */
	const [storeSortField, setStoreSortField] = useState<string>('');

	/* Build store-level attribute column names for enrichment */
	const storeAttrFields = useMemo(() => {
		if (!storeColumns.length) return [] as string[];
		return storeColumns.map((c) => c.columnName);
	}, [storeColumns]);

	/* 3b) Store list derived from cross-filtered data */
	const storeList = useMemo(() => {
		const source = dataForStoreFilter.length ? dataForStoreFilter : allOrderData;
		if (!source.length) return [];
		const seen = new Map();
		source.forEach((row) => {
			const st = String(row[storeFieldName] || '');
			if (st && !seen.has(st)) {
				const storeObj: Record<string, any> = { [storeFieldName]: st };
				if (storeDescFieldName) storeObj[storeDescFieldName] = row[storeDescFieldName] || '';
				storeAttrFields.forEach((f) => {
					if (row[f] !== undefined) storeObj[f] = row[f];
				});
				seen.set(st, storeObj);
			}
		});
		const list = Array.from(seen.values());

		if (storeSortField) {
			list.sort((a, b) => {
				const va = String(a[storeSortField] || '').toLowerCase();
				const vb = String(b[storeSortField] || '').toLowerCase();
				const na = parseFloat(va),
					nb = parseFloat(vb);
				if (!isNaN(na) && !isNaN(nb)) return na - nb;
				return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
			});
		}

		return list;
	}, [dataVersion, storeFieldName, storeDescFieldName, storeAttrFields, dataForStoreFilter, storeSortField]);

	const currentStore = storeList[storeIndex] || null;
	const storeId = currentStore ? String(currentStore[storeFieldName] || '') : '';

	const prevStoreIdRef = useRef<string>('');
	useEffect(() => {
		if (!storeList.length) return;
		if (prevStoreIdRef.current) {
			const newIdx = storeList.findIndex((s) => String(s[storeFieldName] || '') === prevStoreIdRef.current);
			if (newIdx >= 0 && newIdx !== storeIndex) {
				setStoreIndex(newIdx);
				return;
			}
		}
		if (storeIndex >= storeList.length) setStoreIndex(0);
	}, [storeList]);

	useEffect(() => {
		prevStoreIdRef.current = storeId;
	}, [storeId]);

	const handleStoreNavigate = useCallback(
		(dir) => {
			const next = storeIndex + dir;
			if (next >= 0 && next < storeList.length) setStoreIndex(next);
		},
		[storeIndex, storeList.length]
	);

	/* 4) Grid data for selected store */
	const storeOrderData = useMemo(() => {
		if (!storeId || !allOrderData.length) return [];
		return fullyFilteredData.filter((row) => String(row[storeFieldName] || '') === storeId);
	}, [storeId, fullyFilteredData, storeFieldName]);

	/* All rows for selected store (unfiltered) — used by DistributeModal for attribute discovery */
	const allStoreOrderData = useMemo(() => {
		if (!storeId || !allOrderData.length) return [];
		return allOrderData.filter((row) => String(row[storeFieldName] || '') === storeId);
	}, [storeId, allOrderData, storeFieldName]);

	/* Total ordered qty for current store */
	const storeTotalOrderQty = useMemo(() => {
		if (!storeId || !allOrderDataRef.current.length) return 0;
		const keys = Object.keys(allOrderDataRef.current[0]);
		const orderKey = keys.find((k) => /^orderqty$/i.test(k)) || keys.find((k) => /order.*qty/i.test(k));
		let total = 0;
		allOrderDataRef.current.forEach((row) => {
			if (String(row[storeFieldName] || '') === storeId) {
				total += Number(row[orderKey] || 0);
			}
		});
		return total;
	}, [metricsVersion, storeId, storeFieldName]);

	/* No availability fetch — availability info is in the grid */
	const applyDynamicFilter = useCallback(() => {}, []);

	/* ─── Mass update helper ─── */
	const massUpdateOrderQty = useCallback(
		(updateFn: (row: any) => number | null) => {
			const orderKey =
				Object.keys(allOrderDataRef.current[0] || {}).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			const bulkEdits: { RowIdentifier: any; OrderQty: number }[] = [];
			let lastRow = null;

			allOrderDataRef.current = allOrderDataRef.current.map((row) => {
				if (String(row[storeFieldName]) !== storeId) return row;
				const newQty = updateFn(row);
				if (newQty === null) return row;
				const updated = { ...row, [orderKey]: newQty };
				bulkEdits.push({ RowIdentifier: row['OrderDetailID'], OrderQty: newQty });
				lastRow = updated;
				return updated;
			});

			if (bulkEdits.length > 0 && lastRow) {
				if (debouncedSendBulk?.cancel) debouncedSendBulk.cancel();
				bulkBuffer.current = {};
				const editsMap: Record<string, any> = {};
				bulkEdits.forEach((e) => {
					editsMap[e.RowIdentifier] = e;
				});
				flushSaveRef.current?.(editsMap, lastRow, orderKey);
			}

			bumpData();
		},
		[storeId, storeFieldName, bumpData, debouncedSendBulk]
	);

	const handleApplyMass = useCallback(() => {
		if (!storeId || !allOrderData.length) return;
		if (massMetric === 'flat') {
			let qty = massBaseQty > 0 ? massBaseQty : 0;
			if (qty === 0) {
				massUpdateOrderQty(() => 0);
				return;
			}
			massUpdateOrderQty(() => qty);
		}
	}, [massMetric, massBaseQty, storeId, dataVersion, massUpdateOrderQty]);

	const handleOrderQtyChange = useCallback(
		(rowIdx: number, skuId: string, newQty: number) => {
			if (!allOrderData.length) return;
			const orderKey = Object.keys(allOrderData[0]).find((k) => /^orderqty$/i.test(k)) || 'OrderQty';
			const skuKey = Object.keys(allOrderData[0]).find((k) => /^sku$/i.test(k)) || 'SKU';

			const data = allOrderDataRef.current;
			let editedRow = null;
			for (let i = 0; i < data.length; i++) {
				if (String(data[i][storeFieldName]) === storeId && String(data[i][skuKey]) === skuId) {
					data[i][orderKey] = newQty;
					editedRow = data[i];
					break;
				}
			}

			if (editedRow) {
				const rowId = editedRow['OrderDetailID'];
				const existing = bulkBuffer.current[rowId] || { RowIdentifier: rowId };
				bulkBuffer.current[rowId] = { ...existing, OrderQty: newQty };
				lastEditRow.current = editedRow;
				debouncedSendBulk();
			}

			bumpMetrics();
		},
		[dataVersion, storeId, storeFieldName, bumpMetrics, debouncedSendBulk]
	);

	const isDistribute = massMetric === 'distribute';
	const canApply = massBaseQty >= 0;

	/* Groupable columns for grid — SKU + warehouse columns */
	const groupableColumns = useMemo(
		() => [...skuColumns, ...warehouseColumns].map((c) => ({ key: c.columnName, label: c.displayName })),
		[skuColumns, warehouseColumns]
	);

	/* Handle distribute modal apply — distributes by SKU attribute */
	const handleDistributeApply = useCallback(
		(skuQtyMap: Record<string, number>) => {
			if (!allOrderData.length) return;
			const skuKey = Object.keys(allOrderData[0]).find((k) => /^sku$/i.test(k)) || 'SKU';
			massUpdateOrderQty((row) => {
				const sku = String(row[skuKey] || '');
				return sku in skuQtyMap ? skuQtyMap[sku] : null;
			});
		},
		[dataVersion, storeId, storeFieldName, massUpdateOrderQty]
	);

	return (
		<div className="obs-bystore-wrapper">
			{dataLoading && <Dot9loader overlay message="Loading order data..." />}
			{backgroundLoading && <div className="obs-bg-loading-bar">Loading remaining records...</div>}
			<div className="obs-split-layout">
				<div className="obs-grid-area">
					<AgStoreOrderGrid
						data={storeOrderData}
						onOrderQtyChange={handleOrderQtyChange}
						groupableColumns={groupableColumns}
						storeLabel={storeId}
						storeDesc={currentStore?.[storeDescFieldName] || ''}
						storeTotalOrdered={storeTotalOrderQty}
					/>
				</div>
				<div className="obs-right-panel">
					<StoreSelectionBox
						storeList={storeList}
						storeIndex={storeIndex}
						onNavigate={handleStoreNavigate}
						onSelectIndex={(idx) => setStoreIndex(idx)}
						loading={dataLoading}
						totalOrderQty={storeTotalOrderQty}
						sortOptions={storeColumns}
						sortField={storeSortField}
						onSortChange={setStoreSortField}
					/>
					{storeColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Stores"
							icon="🏪"
							accentColor="#1565c0"
							columns={storeColumns}
							gridData={dataForStoreFilter}
							activeKeys={storeActiveKeys}
							onToggleKey={handleStoreToggle}
							filterValues={storeFilterValues}
							onValueChange={(k, v) => setStoreFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Stores"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{skuColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter SKUs"
							icon="🏷"
							accentColor="#7b1fa2"
							columns={skuColumns}
							gridData={dataForSkuFilter}
							activeKeys={skuActiveKeys}
							onToggleKey={handleSkuToggle}
							filterValues={skuFilterValues}
							onValueChange={(k, v) => setSkuFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="SKUs"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{warehouseColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Warehouse"
							icon="🏭"
							accentColor="#e65100"
							columns={warehouseColumns}
							gridData={dataForWhFilter}
							activeKeys={whActiveKeys}
							onToggleKey={handleWhToggle}
							filterValues={whFilterValues}
							onValueChange={(k, v) => setWhFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Warehouses"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					{orderColumns.length > 0 && (
						<DynamicFilterBox
							title="Filter Orders"
							icon="📋"
							accentColor="#f57c00"
							columns={orderColumns}
							gridData={dataForOrderFilter}
							activeKeys={orderActiveKeys}
							onToggleKey={handleOrderToggle}
							filterValues={orderFilterValues}
							onValueChange={(k, v) => setOrderFilterValues((p) => ({ ...p, [k]: v }))}
							matchLabel="Orders"
							onApplyFilter={applyDynamicFilter}
						/>
					)}
					<div className="obs-box obs-box--mass">
						<div className="obs-box__title">⚡ Smart Mass Order</div>
						<div className="obs-mass__field">
							<div className="obs-label">Order Method</div>
							<select
								value={massMetric}
								onChange={(e) => {
									setMassMetric(e.target.value);
									if (e.target.value === 'distribute') setDistributeOpen(true);
								}}
								className="obs-box__select"
							>
								<option value="flat">Flat — Same qty to all SKUs</option>
								<option value="distribute">Distribute by SKU Attribute</option>
							</select>
						</div>
						{isDistribute && (
							<div className="obs-mass__actions">
								<button
									onClick={() => setDistributeOpen(true)}
									className="obs-mass__apply-btn obs-mass__apply-btn--active"
								>
									Open Distribution Setup
								</button>
							</div>
						)}
						{!isDistribute && (
							<>
								<div className="obs-mass__inputs">
									<div>
										<div className="obs-label">Qty per SKU</div>
										<input
											type="number"
											min={0}
											step={1}
											value={massBaseQty}
											onChange={(e) => setMassBaseQty(Number(e.target.value) || 0)}
											className="obs-box__input"
										/>
									</div>
								</div>
								<div className="obs-mass__actions">
									<button
										onClick={handleApplyMass}
										disabled={!canApply}
										className={`obs-mass__apply-btn ${canApply ? 'obs-mass__apply-btn--active' : ''}`}
									>
										Apply to All SKUs
									</button>
									<button onClick={() => setMassBaseQty(0)} className="obs-mass__clear-btn">
										Clear
									</button>
								</div>
							</>
						)}
					</div>

					{/* Distribute by SKU Attribute Modal */}
					<DistributeModal
						open={distributeOpen}
						onClose={() => setDistributeOpen(false)}
						skuOrderData={storeOrderData}
						allSkuData={allStoreOrderData}
						skuFieldName={storeFieldName}
						skuId={storeId}
						initialAvail={999999}
						shelfQty={1}
						onApply={handleDistributeApply}
						storeColumns={skuColumns}
					/>
				</div>
			</div>
		</div>
	);
});

OrderByStoreContent.displayName = 'OrderByStoreContent';
export default OrderByStoreContent;
